/**
 * 인증 API 클라이언트
 * Auth Service (auth.ilouli.com)의 인증 API를 호출
 *
 * 2026-01-12: API 분리 작업으로 인증 API를 auth.ilouli.com으로 이전
 */

import { getToken, setToken, removeToken, setUserCache, clearUserCache } from './tokenManager';

// 인증 API 기본 URL (Auth Service)
const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL ||
  'https://auth.ilouli.com';

// 사용자 관리 API URL (Main API - 관리자 기능용)
const USERS_API_URL = process.env.REACT_APP_API_URL ||
  'https://api.ilouli.com';

/**
 * API 요청 헬퍼 (Auth Service용)
 */
const request = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };

  const response = await fetch(`${AUTH_API_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || 'Request failed');
    error.status = response.status;
    throw error;
  }

  return data;
};

/**
 * API 요청 헬퍼 (Main API용 - 사용자 관리)
 */
const usersRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };

  const response = await fetch(`${USERS_API_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || 'Request failed');
    error.status = response.status;
    throw error;
  }

  return data;
};

/**
 * 인증 API
 */
export const authApi = {
  /**
   * 이메일 로그인
   */
  login: async (email, password) => {
    const data = await request('/api/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    if (data.token) {
      setToken(data.token);
    }
    if (data.user) {
      setUserCache(data.user);
    }

    return data.user;
  },

  /**
   * 회원가입
   */
  signup: async (name, email, password) => {
    return await request('/api/signup', {
      method: 'POST',
      body: JSON.stringify({ name, email, password })
    });
  },

  /**
   * 소셜 로그인
   */
  socialLogin: async (provider, socialUser) => {
    // socialUser 객체를 풀어서 백엔드 형식에 맞게 전송
    const data = await request('/api/social-login', {
      method: 'POST',
      body: JSON.stringify({
        provider,
        id: socialUser.id,
        email: socialUser.email,
        name: socialUser.name,
        picture: socialUser.picture
      })
    });

    if (data.token) {
      setToken(data.token);
    }
    if (data.user) {
      setUserCache(data.user);
    }

    return data.user;
  },

  /**
   * 현재 사용자 정보 조회
   */
  getMe: async () => {
    const data = await request('/api/me');
    return data.user;
  },

  /**
   * 로그아웃
   */
  logout: () => {
    removeToken();
    clearUserCache();
  }
};

/**
 * 사용자 관리 API (관리자 전용)
 * Auth Service (auth.ilouli.com)를 사용 - 사용자 데이터가 auth.db에 있음
 */
export const usersApi = {
  /**
   * 전체 사용자 목록 조회
   */
  getAll: async () => {
    const data = await request('/api/users');
    return data.users;
  },

  /**
   * 승인 대기 사용자 목록
   */
  getPending: async () => {
    const data = await request('/api/users/pending');
    return data.users;
  },

  /**
   * 사용자 승인
   */
  approve: async (userId) => {
    return await request(`/api/users/${userId}/approve`, {
      method: 'POST'
    });
  },

  /**
   * 사용자 거절
   */
  reject: async (userId) => {
    return await request(`/api/users/${userId}/reject`, {
      method: 'POST'
    });
  },

  /**
   * 등급 변경
   */
  updateTier: async (userId, tier) => {
    return await request(`/api/users/${userId}/tier`, {
      method: 'PUT',
      body: JSON.stringify({ tier })
    });
  },

  /**
   * 사용자 삭제
   */
  delete: async (userId) => {
    return await request(`/api/users/${userId}`, {
      method: 'DELETE'
    });
  }
};

export default authApi;
